import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { BarChart3, ExternalLink, LogOut, Menu, Package, Settings, Store, Users, Building2, SlidersHorizontal } from "lucide-react";
import { InstallButton } from "../components/InstallButton";
import { useAuth } from "../context/AuthContext";
import { api, publicShopUrl } from "../lib/api";
import { Logo } from "../components/Logo";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { CreateShopCard } from "../components/CreateShopCard";
import { FullLoader } from "../components/ProtectedRoute";

const TOKO_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: BarChart3, end: true, id: "dashboard" },
  { to: "/dashboard/products", label: "Produk", icon: Package, id: "products" },
  { to: "/dashboard/shop", label: "Toko", icon: Store, id: "shop" },
  { to: "/dashboard/account", label: "Pengaturan Akun", icon: Settings, id: "account" },
];
const ADMIN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: BarChart3, end: true, id: "dashboard" },
  { to: "/dashboard/members", label: "Daftar Member", icon: Users, id: "members" },
  { to: "/dashboard/shops", label: "Daftar Toko", icon: Building2, id: "shops" },
  { to: "/dashboard/settings", label: "Pengaturan Aplikasi", icon: SlidersHorizontal, id: "app-settings" },
  { to: "/dashboard/account", label: "Pengaturan Akun", icon: Settings, id: "account" },
];

const Nav = ({ items, onNavigate }) => (
  <nav className="space-y-1">
    {items.map(({ to, label, icon: Icon, end, id }) => (
      <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${id}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
        <Icon className="h-4 w-4" />{label}
      </NavLink>
    ))}
  </nav>
);

const SidebarBody = ({ user, shop, items, onNavigate, logout }) => (
  <div className="flex h-full flex-col p-5">
    <Logo to="/dashboard" />
    <span className="mt-6 mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{user.role === "admin" ? "Admin" : "Toko"}</span>
    <Nav items={items} onNavigate={onNavigate} />
    {shop && (
      <a href={publicShopUrl(shop.slug)} target="_blank" rel="noreferrer" data-testid="sidebar-view-public-shop"
        className="mt-6 flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 hover:border-slate-400 transition-colors">
        <span className="truncate">/{shop.slug}</span><ExternalLink className="h-4 w-4 shrink-0" />
      </a>
    )}
    <div className="mt-auto pt-6 border-t border-slate-100">
      <InstallButton className="w-full mb-4" />
      <div className="flex items-center gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 font-bold text-slate-700 overflow-hidden">
          {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : user.name?.[0]?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user.name}</p>
          <p className="truncate text-xs text-slate-500">{user.email}</p>
        </div>
        <button onClick={logout} data-testid="logout-button" title="Keluar" className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
);

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [shop, setShop] = useState(undefined);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isToko = user.role === "toko";
  const items = isToko ? TOKO_NAV : ADMIN_NAV;

  const reloadShop = useCallback(async () => {
    if (!isToko) return setShop(null);
    try {
      const { data } = await api.get("/shop");
      setShop(data.shop);
    } catch (e) {
      setShop(null);
    }
  }, [isToko]);

  useEffect(() => {
    reloadShop();
  }, [reloadShop]);

  if (shop === undefined) return <FullLoader />;
  const needsShop = isToko && !shop && location.pathname !== "/dashboard/account";
  const props = { user, shop, items, logout };

  return (
    <div className="min-h-screen bg-[#F8FAFC] lg:flex">
      <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-200 bg-white h-screen sticky top-0">
        <SidebarBody {...props} />
      </aside>
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/80 backdrop-blur-md px-4 h-14">
        <Logo to="/dashboard" small />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="rounded-lg p-2 hover:bg-slate-100"><Menu className="h-5 w-5" /></button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 overflow-y-auto"><SidebarBody {...props} onNavigate={() => setOpen(false)} /></SheetContent>
        </Sheet>
      </header>
      <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
        {needsShop ? <CreateShopCard onCreated={setShop} /> : <Outlet context={{ shop, setShop, reloadShop }} />}
      </main>
    </div>
  );
}
